// Build trigger: v2026.6.7 (rebuild #10) - S3 storage IAM-role fix (backend boot). Storage now detects S3 via bucket (IAM mode), supports IAM-role auth, and mocks when truly disabled. No behavior changes for web. Verified: build + full local boot.
// Build trigger: v1.0.9 2026-05-01 (rebuild #3) — CI hardening pt.2: SSH keep-alive added to deploy-staging.yml (ServerAliveInterval 60, ServerAliveCountMax 30) so long-running silent npm/nx/docker steps don't drop the SSH connection mid-build. No business-web behavior changes.
// Build trigger: v1.0.9 2026-05-01 (rebuild #2) — CI hardening: heredoc backtick fix in deploy-staging.yml + GitHub Actions bump (checkout v4→v5, setup-node v4→v5, expo-github-action v8→v9) to clear the Node.js 20 deprecation cliff (Sept 16 2026). No business-web behavior changes; rebuilt for image-tag parity.
// Build trigger: v1.0.9 2026-04-30 — version lockstep bump (apps + root → 1.0.9). Companion release to citizen-mobile UX hardening; no business-web behavior changes, rebuilt for image-tag parity.
// Build trigger: v1.0.7 — billing cross-tenant Stripe customer fix (createCustomerStrict), messages defaults SEED_VERSION 1.2.0 picked up on platform-api boot 2026-04-21
// Build trigger: 2026-04-24 — force business-web redeploy for: translation shield (Node.prototype patches), ErrorBoundary auto-recovery on translator DOM errors, span-wraps on Category-A sites (TonnieCheckedBadge, QuickEditDialog save button, ProfileSection save button).
// Build trigger: v1.0.8 2026-04-27 — version lockstep bump (apps + root → 1.0.8) alongside CoPilot lifecycle hardening landing in journey-web; SCHEMA_VERSION bumped to 0034.
// Build trigger: v1.0.8 2026-04-27 (rebuild #2) — DB migrations 0032/0033/0034 made idempotent after staging deploy aborted on duplicate-column error. Idempotency now non-negotiable per root CLAUDE.md.

// ⚠️  MUST BE THE FIRST IMPORT. Shields React's commit phase from
// DOM mutations performed by browser translators. See the full
// reasoning in `./shared/translation-shield.ts`.
import './shared/translation-shield';

import { StrictMode } from 'react';
import { MotionConfig } from 'framer-motion';
import { createRoot } from 'react-dom/client';
import { SdkProvider, AuthProvider } from '@tonnie/sdk';
import {
  initBusinessObservability,
  getAnalyticsClient,
  getErrorClient,
} from '@/core/lib/observability';
import { runtimeEnv } from '@/core/config/runtime-env';
import App from '@/core/app/App';
import './index.css';

// Initialize observability before React renders
initBusinessObservability();

const analyticsClient = getAnalyticsClient();
const errorClient = getErrorClient();

// ─────────────────────────────────────────────────────────────────────────
// Clerk configuration (R1: dual Clerk apps)
// ─────────────────────────────────────────────────────────────────────────
//
// Tonnie runs TWO independent Clerk apps:
//   - "Tonnie Citizen"  → journey-web + citizen-mobile
//   - "Tonnie Business" → THIS app (business-web)
//
// Each app has its own publishable key and the Business key here MUST be the
// Business Clerk instance — mixing the Citizen key causes Clerk "invalid
// ticket" + silent cross-origin session errors.
//
// Resolution order:
//   1. VITE_BUSINESS_CLERK_PUBLISHABLE_KEY — the business-specific key. The
//      shared E2E env (config/e2e/.env.e2e) is sourced into BOTH portals'
//      dev servers, so it can't use the generic VITE_CLERK_PUBLISHABLE_KEY
//      for business-web (journey-web reads that one for the Citizen app and
//      Vite gives the sourced shell var precedence over this app's .env.local,
//      which would otherwise boot business-web on the Citizen instance).
//   2. VITE_CLERK_PUBLISHABLE_KEY — this app's own per-app key (dev .env.local,
//      and the image's /env.js, which each tier's compose fills from
//      CLERK_BUSINESS_PUBLISHABLE_KEY), where no business-specific override is
//      present.
//
// Satellite mode and cross-origin sign-in redirects are NO LONGER USED
// (removed with the dual-app migration). Sign-in and sign-up happen on
// this same origin — business-web owns its own /login and /signup pages.
const clerkPublishableKey =
  runtimeEnv.get('VITE_BUSINESS_CLERK_PUBLISHABLE_KEY') ||
  runtimeEnv.get('VITE_CLERK_PUBLISHABLE_KEY') ||
  '';

// A built image reads both from the /env.js its container writes at start,
// which refuses to start without them (docker/frontend/write-env-js.sh).
const config = {
  apiBaseUrl: (() => {
    const url = runtimeEnv.get('VITE_API_URL');
    if (!url) {
      if (import.meta.env.PROD) {
        throw new Error('VITE_API_URL is required in production');
      }
      return 'http://localhost:4000';
    }
    return url;
  })(),
  clerkPublishableKey,
};

if (import.meta.env.PROD && !clerkPublishableKey) {
  console.error('[Config] Business Clerk publishable key not set in /env.js');
}

const rootElement = document.getElementById('root');
if (!rootElement) {
  throw new Error('Root element not found');
}

const root = createRoot(rootElement);
root.render(
  <StrictMode>
    <SdkProvider
      config={{
        baseUrl: config.apiBaseUrl,
        // NOTE: No 401 redirect — route guards handle auth redirects.
        // Using onUnauthorized here causes reload loops because SDK hooks
        // fire API calls before Clerk has loaded a token.
      }}
    >
      <AuthProvider
        publishableKey={config.clerkPublishableKey}
        signInUrl="/login"
        signUpUrl="/signup"
        afterSignInUrl="/dashboard"
        afterSignUpUrl="/onboarding"
      >
        {/* Every framer-motion animation honours the reader's reduced-motion
            setting: transforms and layout moves are dropped, fades kept. */}
        <MotionConfig reducedMotion="user">
          <App analyticsClient={analyticsClient} errorClient={errorClient} />
        </MotionConfig>
      </AuthProvider>
    </SdkProvider>
  </StrictMode>
);
